import React, { useEffect, useState } from "react";
import { setLogin, login } from "../../../apis/authentication";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../../../reducers/userReducer";
import { BHUMEET_TEAM_ROLES, GLOBAL_STYLES } from "../../../appConstants";
import { PDRL_ADMIN_ROUTES } from "../../../routes";
import '../../SignUp/components/newSignUp.css';
import coverimage from '../../../assets/loginCover.png';
import BhuMeetLogo from "../../../assets/bhumeet.svg";
import showPasswordIcon from '../../../assets/Eye.svg';
import hidePasswordIcon from "../../../assets/EyeSlash.svg";
import ForgotPassword from "../SubComponents/ForgotPassword";
import ChangePasswordWithOTP from "../../Profile/SubComponents/ProfileSetup/SubComponents/ChangePasswordWithOTP";
import { useSignUpContext } from "../../../context/SignUpContext";

const emailValidation = {
  required: { value: true, message: 'Email is required' },
  maxLength: { value: 50, message: 'Max 50 characters' },
  pattern: { value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}(\.[0-9]{1,3}){3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, message: 'Enter a valid email' },
  format: { value: /^[a-zA-Z0-9.@]+$/, message: 'Special characters are not allowed' }
};

const passwordValidation = {
  required: { value: true, message: 'Password is required' },
  minLength: { value: 8, message: 'At least 8 characters' },
  maxLength: { value: 30, message: 'Max 30 characters' },
  pattern: { value: /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/, message: 'Must include lowercase, uppercase, digit & special char' }
};

const Login = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user?.user);
  const navigate = useNavigate();
  const [showPassword, setshowPassword] = useState(false);
  const { signUpDetails, setSignUpDetails } = useSignUpContext()
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [formErrors, setFormErrors] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ type: "", msg: "" });
  const [openForgotPasswordPopup, setOpenForgotPasswordPopup] = useState(false);
  const [openChangePassPopup, setOpenChangePassPopup] = useState(false);
  const [inputedMail, setInputedMail] = useState('');

  const validateField = (name, value) => {
    const validations = name === 'email' ? emailValidation : passwordValidation;
    if (validations.required.value && !value) return validations.required.message;
    if (validations.maxLength && value.length > validations.maxLength.value) return validations.maxLength.message;
    if (validations.minLength && value.length < validations.minLength.value) return validations.minLength.message;
    if (validations.format && !validations.format.value.test(value)) return validations.format.message;
    if (validations.pattern && !validations.pattern.value.test(value)) return validations.pattern.message;
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    const errorMsg = validateField(name, value);
    setFormErrors(prev => ({ ...prev, [name]: errorMsg }));
  };

  const validateAllFields = () => {
    let isValid = true;
    const newErrors = { ...formErrors };

    // Validate each field
    newErrors.email = validateField('email', formData.email);
    newErrors.password = validateField('password', formData.password);

    setFormErrors(newErrors);

    isValid = !newErrors.email && !newErrors.password && formData.email && formData.password;

    return isValid;
  };

  const isFormValid = () => {
    return Object.values(formErrors).every(msg => !msg) &&
      Object.values(formData).every(val => val.trim() !== "");
  };

  const handleLogin = async () => {
    const isValid = validateAllFields();

    if (!isValid || loading) return;

    setSignUpDetails((prev) => ({
      ...prev,
      redirectLogin: false
    }))
    setLoading(true);
    setError({ type: "", msg: "" });
    localStorage.removeItem("user-details");
    localStorage.removeItem("company-details");
    localStorage.removeItem("phone-verified");
    localStorage.removeItem("email-verified");

    try {
      const loggedInUser = await login(formData.email, formData.password);
      if (loggedInUser) {
        dispatch(setUser(loggedInUser));
        await setLogin(loggedInUser);
      }
    } catch (err) {
      setError({ type: 'authentication_failure', msg: err || "Login failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  useEffect(() => {
    if (error.type === 'authentication_failure') {
      const timer = setTimeout(() => setError({ type: "", msg: "" }), 5000);
      return () => clearTimeout(timer);
    }
  }, [error.type]);

  useEffect(() => {
    if (user) {
      if (user.is_pdrl_admin) {
        switch (user.bhumeet_role) {
          case BHUMEET_TEAM_ROLES.ADMIN.NAME: navigate(PDRL_ADMIN_ROUTES.ADMIN_DASHBOARD); break;
          case BHUMEET_TEAM_ROLES.FINANCE.NAME: navigate(PDRL_ADMIN_ROUTES.FINANCE_DASHBOARD); break;
          case BHUMEET_TEAM_ROLES.CUSTOMER_SUPPORT_ENGINEER.NAME: navigate(PDRL_ADMIN_ROUTES.SUCCESS_ENGI_DASHBOARD); break;
          case BHUMEET_TEAM_ROLES.MARKETING.NAME: navigate(PDRL_ADMIN_ROUTES.MARKETINGDASHBOARD); break;
          case BHUMEET_TEAM_ROLES.SALES.NAME: navigate(PDRL_ADMIN_ROUTES.SALES_DASHBOARD); break;
          case BHUMEET_TEAM_ROLES.TELECALLER.NAME: navigate(PDRL_ADMIN_ROUTES.TelecallerDashboard); break;
          case BHUMEET_TEAM_ROLES.TELECALLER_SALES.NAME: navigate(PDRL_ADMIN_ROUTES.TeleCallerSalesDashBoard); break;
          default: break;
        }
        localStorage.setItem("dsp_id", "admin");
      } else {
        navigate(user?.dsp?.is_verified ? "/" : signUpDetails?.redirectLogin ? "/login" : "/onboarding/form");
      }
    }

  }, [user, navigate]);

  return (
    <>
      <ForgotPassword
        open={openForgotPasswordPopup}
        setOpenChangePassPopup={(showPopup, email) => {
          setOpenChangePassPopup(showPopup);
          setInputedMail(email);
        }}
        onClose={() => {
          setOpenForgotPasswordPopup(false);
        }}
      />
      <ChangePasswordWithOTP
        open={openChangePassPopup}
        showForgotPasswordPopup={(show) => setOpenForgotPasswordPopup(show)}
        onClose={() => {
          setOpenChangePassPopup(false);
        }}
        userEmail={inputedMail}
      />
      <div className="signupform-container">
        <div className="first_section">
          <div className="banner-heading-wrapper">
            <p className="banner-heading">Simplest way to manage your drone business</p>
            <span className="banner-sub-heading">Sign up now and explore more</span>
          </div>
          <img src={coverimage} alt="Cover" className="cover-image" />
        </div>
        <div className="second_section">
          <div className="form_wrapper">
            <div className="signup-form-wrapper">
              <img src={BhuMeetLogo} alt="BhuMeet Logo" width={160} className="signup-logo" />
              <div className="signup-form-heading">
                <p className="signUpform-title">Log in to your account</p>
              </div>
              <div className="signup-form">
                <div className="form-input-field">
                  <input
                    type="text"
                    name="email"
                    placeholder="Email id"
                    value={formData.email}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    required
                  />
                  <div className="error-text">{formErrors.email}</div>
                </div>
                <div className="form-input-field">
                  <div className="relative w-full">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      required
                    />
                    <div className="absolute right-3 top-2">
                      <img onClick={() => {
                        setshowPassword(!showPassword);
                      }}
                        className="cursor-pointer"
                        width={18}
                        height={18}
                        src={showPassword ? hidePasswordIcon : showPasswordIcon}
                        alt="password-show-hide-icon" />
                    </div>
                  </div>
                  <div className="error-text">{formErrors.password}</div>
                </div>
                <div className="w-full flex justify-end">
                  <span
                    onClick={() => setOpenForgotPasswordPopup(true)}
                    className="font-manrope text-[13px] text-[#0F5EFF] font-[600] cursor-pointer"
                  >
                    forgot password?
                  </span>
                </div>
                {error.msg && <div className="error-text">{error.msg}</div>}
                <div style={{ marginTop: "8px", marginBottom: "8px" }} >
                  <button
                    disabled={!isFormValid() || loading}
                    onClick={handleLogin}
                    className={`w-full text-white rounded-[8px] text-[16px] font-[500] font-manrope h-[40px] text-center ${(!isFormValid() || loading) ? "bg-[#0F5EFF] opacity-60 cursor-not-allowed" : "bg-[#0F5EFF] cursor-pointer"}`} >
                    Login
                  </button>
                  {/* <Button
                    variant="filled"
                    title={"Login"}
                    color="#0F5EFF"
                    width="100%"
                    disabled={!isFormValid() || loading}
                    hoverColor="#1251D2"
                    isRightIcon={false}
                    handleClick={handleLogin}
                  /> */}
                </div>
                <div className="signUp-footer">
                  <p className="footer-text-header">
                    By continuing, you agree to our{" "}
                    <a href="https://bhumeet.app/terms-and-conditions/">Terms</a> and{" "}
                    <a href="https://bhumeet.app/privacy-policy/">Privacy Policy</a>
                  </p>
                  <span className="footer-text-subheader" style={{ marginTop: "5px" }}>
                    Don't have an account?{" "}
                    <Link
                      to={"/signup"}
                      style={{
                        color: GLOBAL_STYLES.BG_ACTIVE,
                        textDecoration: "none",
                        fontWeight: 700
                      }}
                    >
                      Sign up
                    </Link>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;